import axiosIns, { setAxiosAuthHeader } from '@lib/axios';
import {
  clearChallengeStorage,
  isChallengeBootstrap,
  keycloakIns,
  markChallengeSessionExpired,
} from '@lib/keycloak';
import { restartAfterOfficeRefusal } from '@lib/activeOffice';
import { endCallSession, isCallSessionTab } from '@lib/callSession';
import { clearMediaSession, renewMediaSession } from '@lib/mediaSession';
import { logger } from '@src/lib/logger';
import { useAuthStore } from '@stores/authStore';
import { useToastStore } from '@stores/toastStore';
import { isOfficeRefusal } from '@therame/api-client';
import type Keycloak from 'keycloak-js';
import { ref } from 'vue';

/**
 * True from the moment a logout starts until the page redirects away. Drives the
 * full-screen loading overlay in App.vue so logout — which awaits the push
 * unsubscribe and then a Keycloak redirect — never looks unresponsive. A module
 * ref (not a store) so it stays valid across the involuntary-logout call sites
 * that run outside a component/Pinia-active context.
 */
export const isLoggingOut = ref(false);

/**
 * Session orchestration extracted out of the `lib/` leaf layer (TM-9047).
 *
 * `lib/` adapts third-party packages (keycloak-js, axios) and must not reach up
 * into Pinia stores. Everything here touches `authStore`/`toastStore` or wires
 * store-aware behavior onto the leaf axios/keycloak instances, so it lives in
 * `composables/` (composables → stores/lib is allowed; lib → composables is not).
 *
 * These are deliberately plain functions, not a `useSession()` hook: several
 * call sites run outside a component setup context (the axios interceptor,
 * `stores/index.ts`, `callStore.ts`). `useStore()` calls are made lazily inside
 * each function body, which works anywhere once Pinia is active.
 */

/**
 * Updates the Keycloak token if needed.
 * @param keycloak The Keycloak instance.
 */
export const updateKeycloakToken = async (keycloak: Keycloak) => {
  try {
    const refreshed = await keycloak.updateToken(70);
    if (refreshed && keycloak.token) {
      setAxiosAuthHeader(keycloak.token);
      const authStore = useAuthStore();
      if (!authStore.authContext) {
        throw new Error('Auth context is not initialized');
      }
      authStore.authContext.token = keycloak.token;
      // Slide the media cookie with the session: a 401 on a media element
      // never reaches JS, so it cannot be renewed on failure (ADR-0038).
      void renewMediaSession();
    }
  } catch (error) {
    logger.error({ msg: 'Failed to refresh Keycloak token:', error });
    // Not awaited: every api-client request awaits this refresh, and logout
    // waits on such requests (a media-cookie mint in flight). Awaiting it here
    // would make refresh and logout wait on each other and never redirect.
    // Requests released early carry a dead token; their 401 hits the guard.
    void logout({ expired: true });
  }
};

/**
 * Logs the user out.
 *
 * @param options.expired Pass `true` only from involuntary auth-failure paths
 * (token refresh failed, a 401 interceptor fired). It surfaces the "Sitzung
 * abgelaufen" banner on the challenge landing. Deliberate logout (UI buttons)
 * must omit it so the participant lands on a clean form.
 */
export const logout = async (options?: { expired?: boolean }) => {
  // Re-entry guard. An expired session 401s every in-flight request at once, and
  // each failure reaches the api-client's `onUnauthorized` independently — so
  // without this the teardown below runs once per failed request and races
  // several Keycloak redirects against each other (TM-9401).
  if (isLoggingOut.value) return;
  isLoggingOut.value = true;
  // A TAN tab (#1856) ends only itself: no Keycloak logout, no stored tokens
  // removed, so a signed-in Account stays signed in in its other tabs.
  if (isCallSessionTab()) {
    try {
      await useAuthStore().logout(true);
    } catch (e) {
      logger.error({ msg: 'Error during local TAN session cleanup', error: e });
    }
    endCallSession();
    return;
  }
  try {
    const authStore = useAuthStore();
    // Public endpoint, so this works even with an expired token. Must run
    // before the redirect (the logout's Clear-Site-Data spares cookies) and
    // before anything below that may throw. Never throws itself.
    await clearMediaSession();
    // Awaited so it finishes while the token is still valid — before the token
    // removal below and the Keycloak redirect at the end of this function.
    // `expired` is passed as `noAjaxCall`: on an involuntary expiry the token is
    // already dead, so we skip the authenticated teardown AND skip the push
    // unsubscribe — the subscription is kept so the next login gets the
    // "switch account?" prompt. A deliberate logout (no `expired`) runs the full
    // teardown, which unsubscribes push and deletes it on the backend.
    await authStore.logout(options?.expired);
    window.localStorage.removeItem('access_token');
    window.localStorage.removeItem('refresh_token');
    clearChallengeStorage();
  } catch (e) {
    logger.error({ msg: 'Error during local logout cleanup', error: e });
  }

  // The `therame-challenge` client is direct-grant only (standard flow
  // disabled), so a challenge participant can neither do an interactive
  // Keycloak logout round-trip nor re-login through it. Their only recovery is
  // to re-enter the access code, so send them straight back to the landing page.
  if (isChallengeBootstrap) {
    if (options?.expired) {
      markChallengeSessionExpired();
    }
    window.location.replace('/challenge');
    return;
  }

  try {
    await keycloakIns.logout({ redirectUri: window.location.origin });
  } catch (error) {
    logger.error({ msg: 'Keycloak logout failed', error });
    // Redirect failed, so we stay on the page — clear the overlay so the user
    // isn't stuck behind it. On every success path the redirect navigates away
    // and the overlay simply disappears with the old page.
    isLoggingOut.value = false;
  }
};

/**
 * De-duplicates concurrent refreshes. The realm rotates refresh tokens
 * single-use (`revokeRefreshToken: true`, `refreshTokenMaxReuse: 0`), so a burst
 * of requests each calling `updateToken` would race: the first rotates the
 * refresh token and the rest present one that Keycloak has already revoked,
 * failing the refresh and logging the user out (TM-9401).
 */
let inFlightRefresh: Promise<void> | undefined;

/**
 * Ensures the access token is valid for at least the next 70 s, refreshing it if
 * not. Safe to call on every request: `updateToken` resolves without a network
 * round-trip while the token is still comfortably valid.
 */
export const ensureFreshToken = async (keycloak: Keycloak): Promise<void> => {
  if (!inFlightRefresh) {
    inFlightRefresh = updateKeycloakToken(keycloak).finally(() => {
      inFlightRefresh = undefined;
    });
  }
  return inFlightRefresh;
};

let refreshTokenIntervalId: ReturnType<typeof setInterval> | undefined;
let resumeRefreshHandler: (() => void) | undefined;

const clearTokenRefreshInterval = () => {
  if (refreshTokenIntervalId !== undefined) {
    clearInterval(refreshTokenIntervalId);
    refreshTokenIntervalId = undefined;
  }
  if (resumeRefreshHandler) {
    document.removeEventListener('visibilitychange', resumeRefreshHandler);
    window.removeEventListener('focus', resumeRefreshHandler);
    resumeRefreshHandler = undefined;
  }
};

export const stopKeycloakTokenRefresh = () => {
  clearTokenRefreshInterval();
};

export const startKeycloakTokenRefresh = (keycloak: Keycloak) => {
  clearTokenRefreshInterval();

  // Refresh straight away rather than only on the first tick. `setInterval`
  // alone leaves a window of up to 60 s — after a wake-up, exactly when the
  // token is most likely already dead — where requests go out unrefreshed.
  void ensureFreshToken(keycloak);

  refreshTokenIntervalId = setInterval(() => {
    void ensureFreshToken(keycloak);
  }, 60000);

  // Refresh on resume too. The interval does not run while the OS is suspended
  // or the tab is frozen, so returning to the app is precisely when the token is
  // most likely expired — and vue-query's `refetchOnWindowFocus` fires a burst
  // of queries at that same moment. This no longer has to *win* that race: the
  // request interceptor awaits `ensureFreshToken` per request. It just avoids
  // making every one of those requests wait on the same refresh.
  resumeRefreshHandler = () => {
    if (document.visibilityState !== 'visible') return;
    void ensureFreshToken(keycloak);
  };
  document.addEventListener('visibilitychange', resumeRefreshHandler);
  window.addEventListener('focus', resumeRefreshHandler);
};

// Avoid leaking refresh timers during Vite hot module replacement cycles.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    clearTokenRefreshInterval();
  });
}

/**
 * Registers the response interceptor on the leaf `axiosIns`. Called once from
 * `main.ts` at the very start of bootstrap, replacing the former module-load
 * side-effect in `lib/axios.ts` so the leaf no longer needs to reach into
 * stores. Behavior is identical to the previous interceptor.
 */
export const installApiInterceptors = () => {
  axiosIns.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // Network error
      if (!error.response) {
        const toastStore = useToastStore();
        toastStore.addToast({
          text: 'Fehler beim Senden der Daten.',
          buttonAction: () => axiosIns.request(error.config),
          buttonText: 'Erneut versuchen',
          type: 'error',
        });
        return Promise.resolve(error);
      }

      // Unauthorized. This used to also require `error.message === 'Unauthorized'`,
      // which axios never sets — it always reports "Request failed with status
      // code 401" — so the branch was dead (TM-9401). The `authenticated` check
      // keeps a 401 from a not-yet-logged-in app out of the involuntary-logout
      // path; the router guard owns that case.
      if (error.response?.status === 401 && (keycloakIns.authenticated || isCallSessionTab())) {
        logout({ expired: true });
      }
      if (isOfficeRefusal(error)) {
        restartAfterOfficeRefusal(keycloakIns.subject);
      }
      return Promise.reject(error);
    },
  );
};
